import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, CssBaseline, useMediaQuery, BottomNavigation, BottomNavigationAction, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import getTheme from './theme/theme';
import Sidebar from './components/Navigation/Sidebar';
import AppWebBackground from './components/Layout/AppWebBackground';
import ContactDialogProvider from './components/Contacts/ContactDialogProvider';
import { ContactsProvider } from './contexts/ContactsContext';
import './i18n';
import './theme/colorTransitions.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  getBottomNavigationContainerStyles,
  getBottomNavigationStyles,
  getMainContentWithBottomNavStyles
} from './theme/app.styles';

// Lazy loaded pages and components
const Contacts = lazy(() => import('./pages/Contacts'));
const Account = lazy(() => import('./pages/Account'));
const Chat = lazy(() => import('./pages/Chat'));
const ContactsFeed = lazy(() => import('./pages/ContactsFeed'));
const Landing = lazy(() => import('./pages/Landing'));
const ChatHelper = lazy(() => import('./components/Chat/ChatHelper'));

// Component to check authentication
const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

// Wrap components that need location
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  
  // Load saved preferences from localStorage or use defaults
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const theme = getTheme(themeMode, 'intouch');
  const isMobile = useMediaQuery('(max-width: 899px)');

  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user'); // Clear invalid data
      }
    }

    if (token) {
      import('./services/api')
        .then(({ authAPI }) => authAPI.getCurrentUser())
        .then((freshUser) => {
            localStorage.setItem('user', JSON.stringify(freshUser));
            setUser(freshUser);
        })
        .catch((error) => {
          console.error('Error refreshing current user:', error);
        });
    }
    
    // Добавляем слушатель события обновления пользователя
    const handleUserUpdated = (event) => {
      if (event.detail && event.detail.user) {
        setUser(event.detail.user);
      }
    };
    
    document.addEventListener('user-updated', handleUserUpdated);
    
    // Очищаем слушатель при размонтировании компонента
    return () => {
      document.removeEventListener('user-updated', handleUserUpdated);
    };
  }, []);



  useEffect(() => {
    localStorage.setItem('theme', themeMode);
    
    // Применяем класс dark-mode к body при переключении темы
    if (themeMode === 'dark') {
      document.body.classList.add('dark-mode');
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.body.setAttribute('data-theme', 'light');
    }
  }, [themeMode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
        position: 'relative',
      }}
           className={`color-transition-background ${themeMode === 'dark' ? 'dark-mode' : ''}`}>
        {user && !isLoginPage && <AppWebBackground />}

        {/* Sidebar - только на десктопе */}
        {user && !isLoginPage && !isMobile && (
          <Sidebar />
        )}

        {user && !isLoginPage && (
          <Suspense fallback={null}>
            <ChatHelper />
          </Suspense>
        )}
        <Box
          component="main"
          className="color-transition-content"
          sx={{
            flexGrow: 1,
            pt: user && !isLoginPage ? 2 : 0,
            ml: !isMobile && user && !isLoginPage ? '72px' : 0,
            position: 'relative',
            zIndex: 1,
            transition: 'margin-left 0.3s ease',
            ...getMainContentWithBottomNavStyles(
              user && !isLoginPage && location.pathname !== '/chat'
            )
          }}
        >
          <ContactDialogProvider>
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}><CircularProgress /></Box>}>
            <Routes>
              <Route path="/login" element={<Landing setUser={setUser} />} />
              <Route path="/account" element={
                <PrivateRoute>
                  <Account
                    user={user}
                    theme={themeMode}
                    setTheme={setThemeMode}
                  />
                </PrivateRoute>
              } />
              <Route path="/actions" element={<Navigate to="/account" replace />} />
              <Route path="/profile" element={<Navigate to="/account" replace />} />
              <Route path="/contacts" element={
                <PrivateRoute>
                  <Contacts
                    user={user}
                  />
                </PrivateRoute>
              } />

              <Route path="/chat" element={
                <PrivateRoute>
                  <Chat />
                </PrivateRoute>
              } />
              <Route path="/" element={
                <PrivateRoute>
                  <ContactsFeed user={user} />
                </PrivateRoute>
              } />
              <Route path="/feed" element={
                <PrivateRoute>
                  <ContactsFeed user={user} />
                </PrivateRoute>
              } />

              {/* Redirect old routes */}
              <Route path="/dashboard" element={<Navigate to="/account" replace />} />
              <Route path="/maps" element={<Navigate to="/contacts" replace />} />
            </Routes>
            </Suspense>
          </ContactDialogProvider>
        </Box>
        
        {/* Bottom Navigation - только на мобильных, скрыта на странице Chat */}
        {user && !isLoginPage && location.pathname !== '/chat' && isMobile && (
          <Box
            component="nav"
            data-testid="bottom-navigation"
            aria-label="Bottom navigation"
            sx={getBottomNavigationContainerStyles(theme)}
          >
            <BottomNavigation
              value={location.pathname}
              onChange={(event, newValue) => {
                navigate(newValue);
              }}
              showLabels
              sx={getBottomNavigationStyles(theme)}
            >
              <BottomNavigationAction
                label="Find"
                value="/"
                icon={<ViewStreamIcon />}
                data-tour="feed-link"
              />
              <BottomNavigationAction
                label="People"
                value="/contacts"
                icon={<GroupIcon />}
                data-tour="contacts-link"
              />
              <BottomNavigationAction
                label="Preferences"
                value="/account"
                icon={<SettingsIcon />}
                data-tour="account-link"
              />
            </BottomNavigation>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Router>
          <ContactsProvider>
            <AppContent />
          </ContactsProvider>
        </Router>
      </LocalizationProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
